import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import React from "react";

import { TitledTabs } from "components/common/titledTabs";
import {
  BasketInfo,
  BasketStatus,
  getBasketStatusChipProps,
  getLoadVariant,
  isActive,
  isComplete,
  isPending,
  refreshMS,
} from "components/crane/utils";
import { useTenantTranslation } from "hooks/formatters";
import { RequirePermissions } from "src/routes/authentication";
import { useTenant } from "hooks/settings";
import {
  Crane,
  LoadRead,
  useDeleteLoadMutation,
  useGetDeployedPlanResultsQuery,
  useListCranesQuery,
  useListLoadsQuery,
} from "src/store/api/generatedApi";
import { CraneChip } from "../CraneChip";

import { BasketStatusChip } from "../BasketStatusChip";
import { LoadModal } from "../LoadModal";
import {
  initialDeploymentTypeToDeploymentTypes,
  mapBasketInfos,
} from "./utils";
import { useNumberSerialiser } from "hooks/serialisers/numbers";

type LoadVariant = "backup" | "out_of_date" | "new" | "next";
type LoadState = "completed" | "queued" | "active";

interface LoadBaseProps {
  name: string;
  mixId: number;
  loadId: number;
  copperGroupWeightPercentMax: number;
  onClick: () => void;
  variant: LoadVariant;
  loadState: LoadState;
  customerHeatId?: string | null;
}

interface WithBaskets {
  baskets: BasketInfo[];
}

type LoadProps = LoadBaseProps & Partial<WithBaskets>;

interface PlanInfo {
  currentPlanId: number | null;
  backupPlanId: number | null;
  nextPlanId: number | null;
}

interface LoadsProps extends PlanInfo {
  onLoadClick: (id: number) => void;
}

interface QueueProps extends LoadsProps {
  activeLoads: LoadRead[];
  pendingLoads: LoadRead[];
  unassignedCranes: Crane[];
}

interface CompletedProps extends LoadsProps {
  loads: LoadRead[];
}

interface TabbedBodyProps extends PlanInfo {
  loads: LoadRead[];
  cranes: Crane[];
}

interface DeleteLoadButtonProps {
  loadId: number;
}

/**
 * Load component
 * The Load Card on the right hand side of the page.
 */
const Load = ({
  name,
  mixId,
  copperGroupWeightPercentMax,
  onClick,
  variant,
  loadState,
  loadId,
  baskets,
  customerHeatId,
}: LoadProps) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const hasBaskets = !!baskets;
  const { format } = useNumberSerialiser();

  const variantBadge =
    variant === "next" || variant === "backup" ? (
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.common.white,
          backgroundColor: theme.palette.info.main,
          borderRadius: 2,
          paddingY: 0.25,
          paddingX: 0.75,
          alignSelf: "flex-start",
        }}
      >
        {t(variant)}
      </Typography>
    ) : variant === "out_of_date" ? (
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.common.white,
          borderRadius: 2,
          backgroundColor: theme.palette.warning.main,
          paddingY: 0.25,
          paddingX: 0.75,
          alignSelf: "flex-start",
        }}
      >
        {t("outOfDate")}
      </Typography>
    ) : null;

  return (
    <Box
      onClick={onClick}
      data-loadstate={loadState}
      data-loadid={loadId}
      sx={{
        boxShadow: theme.shadows[1],
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        paddingTop: 0.75,
        paddingBottom: hasBaskets ? 1 : 0.75,
        paddingX: 1.5,
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        gap: 0.5,
        cursor: "pointer",
        backgroundColor: theme.palette.common.white,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: "medium",
          }}
        >
          {name}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            {customerHeatId ?? `#${mixId}`}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "30px",
              height: "30px",
              backgroundColor: theme.palette.common.white,
              borderRadius: "2px",
              border: `1px solid ${theme.palette.warning.dark}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.warning.dark,
                fontWeight: "bold",
                lineHeight: 1,
                fontSize: "0.65rem",
              }}
            >
              {"Cu"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.warning.dark,
                fontSize: "0.8rem",
                fontWeight: "bold",
                lineHeight: 1.1,
              }}
            >
              {format(copperGroupWeightPercentMax * 100, { decimalPlaces: 0 })}
            </Typography>
          </Box>
        </Box>
      </Box>
      {variantBadge}
      {hasBaskets ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            mt: 0.25,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.7rem",
            }}
          >
            {t("baskets")}:
          </Typography>
          <Stack direction="column" spacing={0.25}>
            {baskets.map((basket) => {
              const props = getBasketStatusChipProps(basket);
              const statusChip =
                props.status === BasketStatus.Assigned ? (
                  <BasketStatusChip
                    status={props.status}
                    assignedName={props.assignedName}
                    assignedUid={props.assignedUid}
                  />
                ) : (
                  <BasketStatusChip status={props.status} />
                );

              return (
                <Box
                  key={basket.basketNumber}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: "bold",
                      minWidth: "16px",
                      fontSize: "0.7rem",
                    }}
                  >
                    #{basket.basketNumber}
                  </Typography>
                  {statusChip}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
};

/**
 * LoadRow component
 * This ithe Load component and the delete button.
 */
const LoadRow = (props: LoadProps) => {
  return (
    <Stack
      sx={{
        alignItems: "center",
        flexDirection: "row",
        gap: 0,
        justifyContent: "space-between",
      }}
    >
      <Load {...props} />
      <RequirePermissions permissions={["crane_supervisor"]}>
        <DeleteLoadButton loadId={props.loadId} />
      </RequirePermissions>
    </Stack>
  );
};

const DeleteLoadButton = ({ loadId }: DeleteLoadButtonProps) => {
  const tenantName = useTenant();
  const [doDeleteLoadMutation] = useDeleteLoadMutation();
  return (
    <IconButton
      data-testid="delete_load"
      onClick={() =>
        void doDeleteLoadMutation({
          tenantName,
          loadId,
        })
      }
      sx={{ paddingRight: 0 }}
    >
      <RemoveCircleOutlineOutlinedIcon />
    </IconButton>
  );
};

export const BasketLoadQueue = () => {
  const tenantName = useTenant();
  const theme = useTheme();
  const createdSince = React.useMemo(
    () => dayjs().subtract(1, "day").toISOString(),
    []
  );

  const activePlanResults = useGetDeployedPlanResultsQuery({
    tenantName,
    planId: "latest",
    period: 1,
    deploymentTypes: initialDeploymentTypeToDeploymentTypes.standard,
  });
  // Shadow query for migration diff coverage (Meticulous). Result unused.
  useGetDeployedPlanResultsQuery({
    tenantName,
    planId: "latest",
    period: 1,
    deploymentTypes: initialDeploymentTypeToDeploymentTypes.standard,
  });

  const nextActivePlanResults = useGetDeployedPlanResultsQuery({
    tenantName,
    planId: "next",
    period: 1,
    deploymentTypes: initialDeploymentTypeToDeploymentTypes.standard,
  });
  // Shadow query for migration diff coverage (Meticulous). Result unused.
  useGetDeployedPlanResultsQuery({
    tenantName,
    planId: "next",
    period: 1,
    deploymentTypes: initialDeploymentTypeToDeploymentTypes.standard,
  });

  const backupPlanResults = useGetDeployedPlanResultsQuery({
    tenantName,
    planId: "latest",
    period: 1,
    deploymentTypes: initialDeploymentTypeToDeploymentTypes.backup,
  });
  // Shadow query for migration diff coverage (Meticulous). Result unused.
  useGetDeployedPlanResultsQuery({
    tenantName,
    planId: "latest",
    period: 1,
    deploymentTypes: initialDeploymentTypeToDeploymentTypes.backup,
  });

  const loads = useListLoadsQuery(
    {
      tenantName,
      createdBefore: null,
      createdSince,
    },
    { pollingInterval: refreshMS }
  );

  const cranes = useListCranesQuery({ tenantName });

  const hasActivePlanData =
    activePlanResults.data !== undefined ||
    (activePlanResults.error &&
      "status" in activePlanResults.error &&
      activePlanResults.error.status === 404);

  const hasNextPlanData =
    nextActivePlanResults.data !== undefined ||
    (nextActivePlanResults.error &&
      "status" in nextActivePlanResults.error &&
      nextActivePlanResults.error.status === 404);

  const hasBackupPlanData =
    backupPlanResults.data !== undefined ||
    (backupPlanResults.error &&
      "status" in backupPlanResults.error &&
      backupPlanResults.error.status === 404);

  if (
    loads.data &&
    cranes.data &&
    hasActivePlanData &&
    hasNextPlanData &&
    hasBackupPlanData
  ) {
    return (
      <Box
        sx={{
          gridRow: "1 / span 2",
          minHeight: 1,
          height: "100%",
          overflow: "hidden",
          backgroundColor: theme.palette.grey[50],
          borderLeftWidth: 1,
          borderLeftColor: theme.palette.divider,
          borderLeftStyle: "solid",
        }}
      >
        <TabbedBody
          loads={loads.data}
          cranes={cranes.data.cranes}
          currentPlanId={activePlanResults.data?.plan_id ?? null}
          backupPlanId={backupPlanResults.data?.plan_id ?? null}
          nextPlanId={nextActivePlanResults.data?.plan_id ?? null}
        />
      </Box>
    );
  }

  return null;
};

const TabbedBody = ({
  loads,
  cranes,
  currentPlanId,
  backupPlanId,
  nextPlanId,
}: TabbedBodyProps) => {
  const { t } = useTenantTranslation();
  const [selectedTabKey, setSelectedTabKey] = React.useState("chargeQueue");
  const [showLoadModalId, setShowLoadModalId] = React.useState<number | null>(
    null
  );

  const onLoadClick = (id: number) => {
    setShowLoadModalId(id);
  };

  const unassignedCranes = cranes.filter((crane) =>
    loads
      .filter(isActive)
      .flatMap((load) => Object.values(load.baskets))
      .filter((basket) => basket.status !== "filled")
      .every((basket) => basket.assigned_to_uid !== crane.user_uid)
  );

  return (
    <>
      <Stack sx={{ height: "100%" }}>
        <TitledTabs
          tabsSx={{ mx: 2 }}
          containerSx={{ mx: 2 }}
          selectedTabKey={selectedTabKey}
          onChange={setSelectedTabKey}
          tabSpecs={[
            {
              title: t("chargeQueue"),
              key: "chargeQueue",
              content: (
                <Queue
                  onLoadClick={onLoadClick}
                  activeLoads={loads.filter((load) => isActive(load))}
                  pendingLoads={loads.filter((load) => isPending(load))}
                  unassignedCranes={unassignedCranes}
                  currentPlanId={currentPlanId}
                  backupPlanId={backupPlanId}
                  nextPlanId={nextPlanId}
                />
              ),
            },
            {
              title: t("history"),
              key: "history",
              content: (
                <Completed
                  onLoadClick={onLoadClick}
                  loads={loads.filter((load) => isComplete(load))}
                  currentPlanId={currentPlanId}
                  backupPlanId={backupPlanId}
                  nextPlanId={nextPlanId}
                />
              ),
            },
          ]}
        />
      </Stack>
      <LoadModal
        onClose={() => {
          setShowLoadModalId(null);
        }}
        loadId={showLoadModalId}
        cranes={cranes}
      />
    </>
  );
};

/**
 * Completed component
 * Displays a list of completed loads
 */
const Completed = ({
  loads,
  onLoadClick,
  currentPlanId,
  backupPlanId,
  nextPlanId,
}: CompletedProps) => {
  const { t } = useTenantTranslation();

  return (
    <Stack data-testid="completedQueue" gap={1} paddingTop={1} paddingX={2}>
      {loads.length > 0 ? (
        loads.map((load) => {
          const {
            steel_grade_name,
            mix_id,
            copper_group_weight_percent_max,
            load_id,
            plan_id,
            customer_heat_id,
          } = load;

          return (
            <Load
              loadState="completed"
              variant={getLoadVariant({
                planId: plan_id,
                currentPlanId,
                backupPlanId,
                nextPlanId,
              })}
              key={load_id}
              loadId={load_id}
              onClick={() => {
                onLoadClick(load_id);
              }}
              name={steel_grade_name}
              mixId={mix_id}
              copperGroupWeightPercentMax={copper_group_weight_percent_max}
              customerHeatId={customer_heat_id}
            />
          );
        })
      ) : (
        <Typography variant="body1">{t("noCompletedCharges")}</Typography>
      )}
    </Stack>
  );
};

const Queue = ({
  activeLoads,
  pendingLoads,
  onLoadClick,
  unassignedCranes,
  currentPlanId,
  backupPlanId,
  nextPlanId,
}: QueueProps) => {
  const { t } = useTenantTranslation();

  return (
    <Stack
      data-testid="chargeQueue"
      sx={{
        gap: 3,
        paddingY: 1,
        paddingX: 2,
        height: "100%",
        overflow: "auto",
      }}
    >
      <Stack data-testid="activeQueue" gap={1}>
        <Typography variant="h6">{t("active")}</Typography>
        {activeLoads.length > 0 ? (
          activeLoads.map((load) => {
            const {
              steel_grade_name,
              mix_id,
              copper_group_weight_percent_max,
              load_id,
              baskets,
              plan_id,
              customer_heat_id,
            } = load;

            const basketInfos = mapBasketInfos(baskets);

            return (
              <LoadRow
                loadState="active"
                variant={getLoadVariant({
                  planId: plan_id,
                  currentPlanId,
                  backupPlanId,
                  nextPlanId,
                })}
                key={load_id}
                loadId={load_id}
                onClick={() => {
                  onLoadClick(load_id);
                }}
                name={steel_grade_name}
                mixId={mix_id}
                copperGroupWeightPercentMax={copper_group_weight_percent_max}
                baskets={basketInfos}
                customerHeatId={customer_heat_id}
              />
            );
          })
        ) : (
          <Typography variant="body1">{t("noActiveCharges")}</Typography>
        )}
      </Stack>
      <Stack data-testid="queuedCharges" gap={1}>
        <Box
          data-testid="cranes"
          sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
        >
          <Typography variant="h6">{t("next")}</Typography>
          {unassignedCranes.map((crane) => (
            <CraneChip
              key={crane.user_uid}
              name={crane.name}
              variant="warning"
            />
          ))}
        </Box>
        {pendingLoads.length > 0 ? (
          pendingLoads.map((load) => {
            const {
              steel_grade_name,
              mix_id,
              copper_group_weight_percent_max,
              load_id,
              plan_id,
              customer_heat_id,
            } = load;

            return (
              <LoadRow
                loadState="queued"
                variant={getLoadVariant({
                  currentPlanId,
                  backupPlanId,
                  planId: plan_id,
                  nextPlanId,
                })}
                key={load_id}
                loadId={load_id}
                onClick={() => {
                  onLoadClick(load_id);
                }}
                name={steel_grade_name}
                mixId={mix_id}
                copperGroupWeightPercentMax={copper_group_weight_percent_max}
                customerHeatId={customer_heat_id}
              />
            );
          })
        ) : (
          <Typography variant="body1">{t("noQueuedCharges")}</Typography>
        )}
      </Stack>
    </Stack>
  );
};
